import {
  CrmFieldService,
  ContactCustomColumns,
  StandardExternalIds,
  StandardIds,
  SystemFieldIds,
} from '@galaxy/crm/services/field-service';
import {
  Inject,
  Injectable,
  OnDestroy,
  signal,
} from '@angular/core';
import {
  ColumnDataInterface,
  TableCustomizationService,
} from './_abstract-table-customization.service';
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  Observable,
  of,
  Subject,
} from 'rxjs';
import {
  CrmDependencies,
  CrmInjectionToken,
  CrmMultiLocationDependencies,
  CrmMultilocationInjectionToken,
} from '../../tokens-and-interfaces';
import { TranslateService } from '@ngx-translate/core';
import {
  map,
  mergeMap,
  take,
  tap,
} from 'rxjs/operators';
import { PAGE_ROUTES } from '../../constants';
import {
  CrmObjectInterface,
  FieldSchemaInterface,
  PhoneFieldsInterface,
} from '@vendasta/crm';
import {
  format,
  ParsedNumber,
  parseNumber,
} from 'libphonenumber-js';
import {
  CustomCell,
  GalaxyColumnDef,
} from '@vendasta/galaxy/table';
import {
  GalaxyFilterDefinitionInterface,
  GalaxyFilterInterface,
  GalaxyFilterOperator,
} from '@vendasta/galaxy/filter/chips';
import { RestrictedGroupService } from '../restricted-group.service';
import { CrmObjectDisplayService } from '../crm-object-display.service';
import { LocationCellComponent } from '../../shared-components/custom-table-cells/multilocation/location.component';

@Injectable()
export class ContactTableCustomizationService extends TableCustomizationService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private contactLinkBuild$$ = new BehaviorSubject('');
  private shouldShowLocationColumn$$ = new BehaviorSubject<boolean>(false);
  private isMultiLocation = signal(false);

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    @Inject(CrmMultilocationInjectionToken) private readonly multiLocationConfig: CrmMultiLocationDependencies,
    protected override readonly crmFieldService: CrmFieldService,
    private readonly translate: TranslateService,
    private readonly restrictedGroupService: RestrictedGroupService,
    private readonly objectDisplayService: CrmObjectDisplayService,
  ) {
    super(crmFieldService);
    const isMultiLocation$ = multiLocationConfig.isMultiLocation$ ?? of(false);
    combineLatest([this.config.viewProfileFlag$, isMultiLocation$])
      .pipe(
        take(1),
        tap(([_, isMultiLocation]) => {
          this.shouldShowLocationColumn$$.next(isMultiLocation);
          this.isMultiLocation.set(isMultiLocation);
        }),
        mergeMap(([featureFlag, isMultiLocation]) => {
          if (!featureFlag) {
            return of('');
          }
          return isMultiLocation ? this.multiLocationConfig.routePrefix$ : this.config.routePrefix$;
        }),
      )
      .subscribe((routePrefix) => {
        if (!routePrefix) {
          return;
        }

        const contactProfileViewRoute = this.isMultiLocation()
          ? `${PAGE_ROUTES.MULTI_LOCATION.CONTACTS.ROOT}/${PAGE_ROUTES.MULTI_LOCATION.CONTACTS.SUBROUTES.PROFILE}`
          : `${PAGE_ROUTES.CONTACT.ROOT}/${PAGE_ROUTES.CONTACT.SUBROUTES.PROFILE}`;

        this.contactLinkBuild$$.next(`${routePrefix}/${contactProfileViewRoute}`);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private hideIDColumns(columns: GalaxyColumnDef[]): GalaxyColumnDef[] {
    const columnIdentifiersToHide = [
      this.crmFieldService.getFieldId(StandardExternalIds.FirstName),
      this.crmFieldService.getFieldId(StandardExternalIds.LastName),
      SystemFieldIds.ContactExternalID,
      SystemFieldIds.ContactID,
      StandardIds.ContactPrimaryCompanyID,
      ContactCustomColumns.FullAddress,
      SystemFieldIds.ContactGroupID,
      SystemFieldIds.ContactOwnerID,
      StandardIds.ContactAdditionalTeamMemberIDs,
      StandardIds.ContactPrimarySalespersonID,
      StandardIds.ContactAdditionalSalespersonIDs
    ];
    for (const identifier of columnIdentifiersToHide) {
      const index = columns.findIndex((c) => c.id === identifier);
      if (index >= 0) {
        columns[index].hidden = true;
      }
    }
    return columns;
  }

  private addPrimaryCompanyNameColumn(columns: GalaxyColumnDef[]): GalaxyColumnDef[] {
    return [
      {
        id: ContactCustomColumns.PrimaryCompanyName,
        title: this.translate.instant('CONTACT.LIST_OBJECTS_TABLE.CUSTOM_COLUMNS.PRIMARY_COMPANY_NAME'),
        hidden: true,
      },
      ...columns,
    ];
  }

  private async addPrimaryCompanyNameRowData(
    crmObject: CrmObjectInterface,
    schemaMap: { [schemaId: string]: FieldSchemaInterface },
    columnData: ColumnDataInterface,
  ): Promise<void> {
    let contactPrimaryCompanyID = '';
    const contactPrimaryCompanyIDFieldValue = this.crmFieldService.getFieldValueFromCrmObject(
      crmObject,
      StandardIds.ContactPrimaryCompanyID,
    );
    if (contactPrimaryCompanyIDFieldValue?.stringValue) {
      contactPrimaryCompanyID = contactPrimaryCompanyIDFieldValue.stringValue;
    }

    // Lazy import to break circular dependency
    const { PrimaryCompanyNameComponent } = await import('../../shared-components/custom-table-cells/primary-company-name.component');
    columnData[ContactCustomColumns.PrimaryCompanyName] = {
      cellType: 'custom',
      value: {
        namespace: crmObject.namespace,
        objectId: contactPrimaryCompanyID,
      },
      component: PrimaryCompanyNameComponent,
    } as CustomCell;
  }

  private addFullNameColumn(columns: GalaxyColumnDef[]): GalaxyColumnDef[] {
    return [
      {
        id: ContactCustomColumns.FullName,
        title: this.translate.instant('CONTACT.LIST_OBJECTS_TABLE.CUSTOM_COLUMNS.FULL_NAME'),
        sticky: true,
        sortable: false,
      },
      ...columns,
    ];
  }

  private formatPhoneNumber(phoneNumber: string): string {
    const parsedNumber = parseNumber(phoneNumber, {
      defaultCountry: 'US',
    });
    if (Object.keys(parsedNumber).length === 0) {
      return '';
    }

    return format(parsedNumber as ParsedNumber, 'INTERNATIONAL');
  }

  private formatPhoneNumberFields(phoneNumber: PhoneFieldsInterface): string {
    if (phoneNumber.nationalNumber === '') {
      return '';
    }
    const parsed = {
      country: phoneNumber.isoCountryCode,
      phone: phoneNumber.nationalNumber,
      ext: phoneNumber.extension,
    } as ParsedNumber;
    const internationalNumber = format(parsed, 'INTERNATIONAL');
    return internationalNumber;
  }

  private addFullNameRowData(
    crmObject: CrmObjectInterface,
    schemaMap: { [schemaId: string]: FieldSchemaInterface },
    columnData: ColumnDataInterface,
  ): void {
    const crmObjectId = crmObject.crmObjectId || '';
    const namespace = crmObject.namespace || '';
    const link = this.isMultiLocation()
      ? this.contactLinkBuild$$.value.replace(':crmObjectId', crmObjectId).replace(':namespace', namespace)
      : this.contactLinkBuild$$.value.replace(':crmObjectId', crmObjectId);

    columnData[ContactCustomColumns.FullName] = {
      cellType: 'text',
      value: this.getRowFullName(crmObject),
      link: link,
    };
  }

  getRowFullName(crmObject: CrmObjectInterface): string {
    return this.objectDisplayService.getObjectDisplayName('Contact', crmObject);
  }

  getAssociationInfo(crmObject: CrmObjectInterface): string {
    return this.objectDisplayService.getAssociationInfo('Contact', crmObject);
  }

  private addFullAddressColumnData(
    crmObject: CrmObjectInterface,
    schemaMap: { [schemaId: string]: FieldSchemaInterface },
    columnData: ColumnDataInterface,
  ): void {
    columnData[ContactCustomColumns.FullAddress] = {
      cellType: 'text',
      value: this.getRowFullAddress(crmObject),
    };
  }

  async customizeRowData(
    crmObject: CrmObjectInterface,
    schemaMap: { [schemaId: string]: FieldSchemaInterface },
    columnData: ColumnDataInterface,
  ): Promise<void> {
    this.customizeNextTaskDueDateCellData(columnData);
    this.addFullNameRowData(crmObject, schemaMap, columnData);
    await this.addPrimaryCompanyNameRowData(crmObject, schemaMap, columnData);
    this.addFullAddressColumnData(crmObject, schemaMap, columnData);

    if (this.shouldShowLocationColumn$$.getValue()) {
      this.addLocationCustomCell(crmObject, columnData);
    }

    this.addCustomCells(this.config.contact?.tableCustomCells || [], crmObject, columnData);
  }

  private customizeNextTaskDueDateCellData(columnData: ColumnDataInterface): void {
    const cell = columnData[this.crmFieldService.getFieldId(StandardIds.ContactNextTaskDueDate)];
    if (cell) {
      cell.cellType = 'timestamp';
    }
  }

  customizeColumns(columns: GalaxyColumnDef[], isMobile: boolean): GalaxyColumnDef[] {
    let customizedColumns = this.hideIDColumns(columns);
    customizedColumns = this.addPrimaryCompanyNameColumn(customizedColumns);
    customizedColumns = this.addFullNameColumn(customizedColumns);
    customizedColumns = this.addFullAddressColumn(customizedColumns);

    if (this.shouldShowLocationColumn$$.getValue()) {
      customizedColumns = this.addLocationColumn(customizedColumns);
    }

    const customCells = this.config.contact?.tableCustomCells || [];
    customizedColumns = this.addCustomColumns(
      customCells.map((c) => c.columnDefinition),
      customizedColumns,
    );
    if (isMobile) {
      customizedColumns = this.customizeColumnsMobile(customizedColumns);
    }
    return [...customizedColumns];
  }

  customizeColumnsMobile(columns: GalaxyColumnDef[]): GalaxyColumnDef[] {
    const columnIdentifiersToShow = new Map<string, boolean>([
      [this.crmFieldService.getFieldId(StandardExternalIds.Email), true],
      [ContactCustomColumns.FullName, true],
    ]);

    columns.forEach((column) => {
      column.hidden = !columnIdentifiersToShow.get(column.id);
    });
    return [...columns];
  }

  getRowFullAddress(crmObject: CrmObjectInterface): string {
    const addressLineOne = this.crmFieldService.getFieldId(StandardIds.ContactPrimaryAddressLine1);
    const addressLineTwo = this.crmFieldService.getFieldId(StandardIds.ContactPrimaryAddressLine2);
    const city = this.crmFieldService.getFieldId(StandardIds.ContactPrimaryAddressCity);
    const state = this.crmFieldService.getFieldId(StandardIds.ContactPrimaryAddressState);
    const postalCode = this.crmFieldService.getFieldId(StandardIds.ContactPrimaryAddressPostalCode);
    const country = this.crmFieldService.getFieldId(StandardIds.ContactPrimaryAddressCountry);

    const addressLineOneData = crmObject.fields?.find((f) => f.fieldId === addressLineOne);
    const addressLineTwoData = crmObject.fields?.find((f) => f.fieldId === addressLineTwo);
    const cityData = crmObject.fields?.find((f) => f.fieldId === city);
    const stateData = crmObject.fields?.find((f) => f.fieldId === state);
    const postalCodeData = crmObject.fields?.find((f) => f.fieldId === postalCode);
    const countryData = crmObject.fields?.find((f) => f.fieldId === country);

    const fullAddress = [
      addressLineOneData?.stringValue,
      addressLineTwoData?.stringValue,
      cityData?.stringValue,
      stateData?.stringValue,
      countryData?.stringValue,
      postalCodeData?.stringValue,
    ];

    return fullAddress.filter((value) => value !== undefined).join(', ');
  }

  private addFullAddressColumn(columns: GalaxyColumnDef[]): GalaxyColumnDef[] {
    return [
      {
        id: ContactCustomColumns.FullAddress,
        title: this.translate.instant('LIST_OBJECTS_TABLE.CUSTOM_COLUMNS.FULL_ADDRESS'),
        hidden: true,
        sortable: false,
      },
      ...columns,
    ];
  }

  private addLocationColumn(columns: GalaxyColumnDef[]): GalaxyColumnDef[] {
    return [
      {
        id: ContactCustomColumns.Location,
        title: 'Location',
      },
      ...columns,
    ];
  }

  private addLocationCustomCell(crmObject: CrmObjectInterface, columnData: ColumnDataInterface): void {
    if (!crmObject.namespace) {
      return;
    }
    columnData[ContactCustomColumns.Location] = {
      cellType: 'custom',
      value: {
        objectId: crmObject.crmObjectId,
        objectType: 'Contact',
        namespace: crmObject.namespace,
      },
      component: LocationCellComponent,
    } as CustomCell;
  }

  private makeGroupFilterRestricted(
    restrictedToGroupId: string,
    filters: GalaxyFilterDefinitionInterface[],
  ): GalaxyFilterDefinitionInterface[] {
    if (restrictedToGroupId) {
      const groupIDIndex = filters.findIndex((f) => f.fieldId === SystemFieldIds.ContactGroupID);
      if (groupIDIndex > -1) {
        filters[groupIDIndex].preset = true;
        filters[groupIDIndex].lockedValue = true;
      }
    }
    return filters;
  }

  customizeFilters(filters: GalaxyFilterDefinitionInterface[]): Observable<GalaxyFilterDefinitionInterface[]> {
    const overrides$ = this.config.contact?.filterInputOverrides$ ?? of([]);
    const appliedOverrides$ = overrides$.pipe(map((overrides) => this.overrideCustomFilters(filters, overrides)));
    return combineLatest([this.restrictedGroupService.getCurrentGroupId(), appliedOverrides$]).pipe(
      map(([restrictedMarketId, filters]: [string, GalaxyFilterDefinitionInterface[]]) =>
        this.makeGroupFilterRestricted(restrictedMarketId, filters),
      ),
    );
  }

  buildInitialFilter(
    filterDefinitions: GalaxyFilterDefinitionInterface[],
    identifier: string,
    filterId: string,
  ): GalaxyFilterInterface {
    const fieldId = this.crmFieldService.getFieldId(identifier);
    const defn = filterDefinitions.find((f) => f.fieldId === fieldId);
    if (defn) {
      defn.preset = true;
    }
    const filter = {
      fieldId: fieldId,
      filterId: filterId,
      operator: GalaxyFilterOperator.FILTER_OPERATOR_IS,
      values: [],
      definition: defn,
    };
    return filter;
  }

  async getInitialFilters(
    filterDefinitions$: Observable<GalaxyFilterDefinitionInterface[]>,
  ): Promise<GalaxyFilterInterface[]> {
    return firstValueFrom(
      combineLatest([
        this.restrictedGroupService.getCurrentGroupId(),
        filterDefinitions$,
        this.config.contact?.initialFilters$ || of([]),
      ]),
    ).then(([selectedGroupId, filterDefinitions, initialFilters]) => {
      const filters = initialFilters.map((filter) => {
        return this.buildInitialFilter(filterDefinitions, filter.fieldId, filter.filterId);
      });
      if (selectedGroupId) {
        const groupIdDefn = filterDefinitions.find(
          (f) => f.fieldId === this.crmFieldService.getFieldId(SystemFieldIds.ContactGroupID),
        );
        if (groupIdDefn) {
          groupIdDefn.preset = true;
        }
        filters.push({
          fieldId: SystemFieldIds.ContactGroupID,
          filterId: 'filterContactByRestrictedGroupID',
          operator: GalaxyFilterOperator.FILTER_OPERATOR_IS,
          values: [
            {
              string: selectedGroupId,
            },
          ],
          definition: groupIdDefn,
        });
      }
      return filters;
    });
  }
}
